import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Compass, PenTool, Ruler, Boxes, Palette, Scissors,
  ArrowRight, ArrowUpRight, Check, MessageCircle, Instagram, Quote,
} from "lucide-react";
import Logo from "@/components/Logo";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";

const WA_LINK = "https://wa.me/919515562436";
const IG_LINK = "https://instagram.com/formline2026";

const HERO_MODEL = "https://images.unsplash.com/photo-1779089614071-93c495af0c89?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DEMO_IMG = "https://images.unsplash.com/photo-1744686909434-fd158fca1c35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const EXAMS = [
  { id: "NATA", icon: Ruler, desc: "National Aptitude Test in Architecture — drawing, aesthetic sensitivity & logical reasoning for B.Arch admissions." },
  { id: "JEE B.Arch", icon: Compass, desc: "Paper 2 of JEE Main — mathematics, aptitude and the all-important freehand drawing section." },
  { id: "UCEED", icon: Boxes, desc: "Undergraduate entrance for B.Des at IITs — visual reasoning, observation and design thinking." },
  { id: "CEED", icon: PenTool, desc: "Common Entrance for Design — the gateway to M.Des at IITs and IISc, built on creativity & problem-solving." },
  { id: "NID", icon: Palette, desc: "National Institute of Design entrance — sketching, material handling and studio-style creative tests." },
  { id: "NIFT", icon: Scissors, desc: "Fashion & design institute test — CAT + situation test with hands-on model making and colour sense." },
];

const PROGRAMS = [
  {
    name: "Crash Course", duration: "2–3 months", price: "₹12,000 – 15,000",
    tag: "Fast-track", featured: false,
    points: ["Exam-focused intensive drills", "Previous-year paper marathons", "Weekend mock tests", "Best for last-mile prep"],
  },
  {
    name: "Standard Program", duration: "6 months", price: "₹22,000 – 25,000",
    tag: "Balanced", featured: false,
    points: ["Full syllabus coverage", "Weekly drawing critique", "Aptitude + reasoning modules", "Monthly graded mocks"],
  },
  {
    name: "Comprehensive Program", duration: "1 year", price: "₹50,000",
    tag: "Most Chosen", featured: true,
    points: ["Everything in Standard, deeper", "1-on-1 portfolio mentoring", "All exams: NATA, JEE, UCEED, NID, NIFT", "Situation-test & studio practice", "EMI available"],
  },
];

const CHAPTERS = [
  { n: "01", title: "Draw daily", body: "Freehand, perspective and observation drills built into every single class — because scores are made at the drawing board." },
  { n: "02", title: "Think like a designer", body: "Aptitude, visual reasoning and design logic taught through real briefs, not rote worksheets." },
  { n: "03", title: "Mock the exam", body: "Timed, graded, exam-pattern mocks with line-by-line critique so nothing surprises you on the day." },
];

const TESTIMONIALS = [
  { quote: "The drawing critiques changed everything. I walked into NATA knowing exactly how examiners think.", name: "Ananya R.", exam: "Cleared NATA 2025" },
  { quote: "From zero design background to a UCEED rank. The mentors kept me accountable every week.", name: "Karthik M.", exam: "UCEED Qualified" },
  { quote: "The situation test practice for NIFT was unreal. Nothing on exam day felt new.", name: "Sara P.", exam: "NIFT Admit" },
];

// kinetic reveal helpers
const lineWrap = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } };
const lineUp = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Overline = ({ children, light }) => (
  <span className={`text-xs uppercase tracking-[0.28em] font-semibold ${light ? "text-white/60" : "text-brand"}`}>
    {children}
  </span>
);

export default function Landing() {
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [program, setProgram] = useState("");

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const modelY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const modelRotate = useTransform(scrollYProgress, [0, 1], [0, 8]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const goTo = (sel) => {
    const el = document.querySelector(sel);
    if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: -64 });
  };

  const openDemo = (prog = "") => { setProgram(prog); setDialogOpen(true); };

  return (
    <div className="relative bg-white text-ink font-body selection:bg-brand">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 border-b border-ink/10 bg-white/80 backdrop-blur-md">
        <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <button onClick={() => goTo("#top")} className="flex items-center gap-3 group" data-testid="nav-logo">
            <Logo className="w-10 h-7" />
            <span className="font-display font-extrabold text-lg tracking-tight leading-none">
              FORMLINE<span className="text-brand">.</span>
            </span>
          </button>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium">
            <button onClick={() => goTo("#programs")} className="hover:text-brand transition-colors" data-testid="nav-programs">Programs</button>
            <button onClick={() => goTo("#exams")} className="hover:text-brand transition-colors" data-testid="nav-exams">Exams</button>
            <button onClick={() => goTo("#voices")} className="hover:text-brand transition-colors" data-testid="nav-testimonials">Results</button>
            <button onClick={() => goTo("#contact")} className="hover:text-brand transition-colors" data-testid="nav-contact">Contact</button>
          </div>
          <Button onClick={() => openDemo("")} data-testid="nav-demo-btn" className="rounded-none bg-brand hover:bg-brand-dark text-white h-10 px-5 text-sm font-semibold">
            Free demo
          </Button>
        </nav>
      </header>

      {/* HERO */}
      <section id="top" ref={heroRef} className="relative min-h-screen grid-paper overflow-hidden pt-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-24 top-1/3 w-[420px] h-[420px] rounded-full bg-brand-soft blur-3xl opacity-60" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center py-16 lg:py-0 min-h-[calc(100vh-4rem)]">
          <div className="lg:col-span-7 relative z-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-brand" />
              <Overline>NATA · JEE B.Arch · UCEED · CEED · NID · NIFT</Overline>
            </motion.div>

            <motion.h1 variants={lineWrap} initial="hidden" animate="show" className="font-display font-black tracking-tighter leading-[0.92] text-[15vw] sm:text-7xl lg:text-[5.6rem]">
              {["Draw your", "way into"].map((l) => (
                <span key={l} className="block overflow-hidden"><motion.span variants={lineUp} className="block">{l}</motion.span></span>
              ))}
              <span className="block overflow-hidden"><motion.span variants={lineUp} className="block text-brand">architecture</motion.span></span>
            </motion.h1>

            <motion.p variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.7 }} className="mt-7 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
              Formline Design Institute is a focused coaching studio for design & architecture entrance exams. Drawing-first teaching, exam-pattern mocks and one-on-one mentoring that get you a seat.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.85 }} className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button onClick={() => goTo("#programs")} data-testid="hero-courses-btn" className="rounded-none bg-brand hover:bg-brand-dark text-white h-14 px-8 text-base font-semibold group">
                View courses &amp; fees <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={() => openDemo("")} data-testid="hero-demo-btn" variant="outline" className="rounded-none border-2 border-brand text-brand hover:bg-brand hover:text-white h-14 px-8 text-base font-semibold bg-transparent transition-colors">
                Book a free demo class
              </Button>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative z-10">
            <motion.div style={{ y: modelY, rotate: modelRotate }} className="relative">
              <div className="border-2 border-ink/10 bg-white p-2 shadow-[16px_16px_0_0_rgba(26,42,143,0.12)]">
                <img src={HERO_MODEL} alt="Architectural study model" className="w-full h-[300px] lg:h-[440px] object-cover grayscale-[0.15]" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-brand text-white px-5 py-3 hidden sm:block">
                <div className="font-display text-2xl font-black leading-none">6</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">exams covered</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y-2 border-brand/20 bg-brand py-4 select-none">
        <Marquee speed={40} gradient={false}>
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i} className="mx-8 font-display text-2xl md:text-3xl font-bold tracking-tight text-white/90 flex items-center gap-8">
              Draw your way into architecture <span className="text-accent text-3xl">✳</span> NATA <span className="text-accent">/</span> JEE B.Arch <span className="text-accent">/</span> UCEED <span className="text-accent">/</span> CEED <span className="text-accent">/</span> NID <span className="text-accent">/</span> NIFT <span className="text-accent text-3xl mr-8">✳</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* PROGRAMS */}
      <section id="programs" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} className="max-w-3xl mb-14">
          <Overline>Programs &amp; fees</Overline>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
            Pick the pace that fits your exam calendar.
          </h2>
          <p className="mt-5 text-muted-foreground text-base md:text-lg">Transparent fees, no hidden charges. Every program includes drawing critique, mock tests and mentor access.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-ink/10">
          {PROGRAMS.map((p, i) => (
            <motion.div
              key={p.name}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              data-testid={`program-card-${i}`}
              className={`relative border-r border-b border-ink/10 p-8 lg:p-10 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${p.featured ? "bg-brand text-white" : "bg-white hover:bg-brand-soft/40"}`}
            >
              {p.featured && (
                <span className="absolute top-0 right-0 bg-accent text-ink text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5">Most Chosen</span>
              )}
              <span className={`text-xs uppercase tracking-[0.24em] font-semibold ${p.featured ? "text-white/60" : "text-brand"}`}>{p.tag}</span>
              <h3 className={`mt-4 font-display text-2xl font-bold ${p.featured ? "text-white" : "text-ink"}`}>{p.name}</h3>
              <div className={`text-sm mt-1 ${p.featured ? "text-white/70" : "text-muted-foreground"}`}>{p.duration}</div>
              <div className="mt-6 font-display text-3xl md:text-4xl font-black tracking-tight">{p.price}</div>
              <ul className="mt-7 space-y-3 flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className={`flex items-start gap-3 text-sm ${p.featured ? "text-white/85" : "text-ink/75"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-accent" : "text-brand"}`} />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => openDemo(p.name)}
                data-testid={`program-enquire-${i}`}
                className={`mt-8 rounded-none h-12 font-semibold group ${p.featured ? "bg-white text-brand hover:bg-accent hover:text-ink" : "bg-brand text-white hover:bg-brand-dark"}`}
              >
                Enquire &amp; book demo <ArrowUpRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EXAMS */}
      <section id="exams" className="bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-3xl mb-14">
            <Overline light>Exams we cover</Overline>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-none text-white">
              One studio. Every design entrance.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/15">
            {EXAMS.map((ex, i) => {
              const Icon = ex.icon;
              return (
                <motion.div
                  key={ex.id}
                  variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                  data-testid={`exam-card-${ex.id}`}
                  className="group border-r border-b border-white/15 p-8 lg:p-10 transition-colors duration-300 hover:bg-brand"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-8 h-8 text-accent" strokeWidth={1.5} />
                    <span className="text-xs uppercase tracking-[0.2em] text-white/40 group-hover:text-white/70 transition-colors">Exam</span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-bold text-white">{ex.id}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed group-hover:text-white/85 transition-colors">{ex.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
          <Overline>How we train</Overline>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-none max-w-2xl">
            A method, not a mass batch.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {CHAPTERS.map((c, i) => (
            <motion.div key={c.n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white p-8 lg:p-10">
              <div className="text-outline font-display text-6xl font-black leading-none">{c.n}</div>
              <h3 className="mt-6 font-display text-2xl font-bold">{c.title}</h3>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DEMO CTA */}
      <section className="relative bg-brand text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={DEMO_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 grid-paper-fine opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Overline light>Free demo class</Overline>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-white">
              Sit in on a class.<br />Draw with us — for free.
            </h2>
            <p className="mt-6 max-w-xl text-white/75 text-base md:text-lg">No commitment. Experience a real Formline session, meet a mentor and get an honest read on where you stand.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Button onClick={() => openDemo("")} data-testid="cta-demo-btn" className="rounded-none bg-white text-brand hover:bg-accent hover:text-ink h-16 px-10 text-lg font-bold w-full lg:w-auto group">
              Book my free demo <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="voices" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14 max-w-2xl">
          <Overline>Student voices</Overline>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-none">Seats earned. Stories told.</h2>
          <p className="mt-4 text-sm text-muted-foreground italic">Placeholder testimonials — to be replaced with verified student results.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.1 }} data-testid={`testimonial-${i}`} className="border border-ink/10 p-8 flex flex-col transition-all duration-300 hover:border-brand hover:-translate-y-1 hover:shadow-[10px_10px_0_0_rgba(26,42,143,0.1)]">
              <Quote className="w-8 h-8 text-brand" />
              <blockquote className="mt-5 text-lg leading-relaxed text-ink/85 flex-1">“{t.quote}”</blockquote>
              <figcaption className="mt-6 pt-5 border-t border-ink/10">
                <div className="font-display font-bold">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-brand mt-1">{t.exam}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* FOOTER / CONTACT */}
      <footer id="contact" className="bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3">
                <Logo className="w-12 h-8" stroke="#ffffff" />
                <span className="font-display font-extrabold text-xl tracking-tight">FORMLINE<span className="text-accent">.</span></span>
              </div>
              <p className="mt-6 max-w-md text-white/60 leading-relaxed">Formline Design Institute — drawing-first coaching for architecture & design entrance exams. Come draw your way in.</p>
            </div>
            <div className="lg:col-span-6 lg:justify-self-end w-full lg:w-auto">
              <Overline light>Get in touch</Overline>
              <div className="mt-5 flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/919515562436" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-2" className="flex items-center justify-center gap-2 bg-[#25D366] text-ink font-semibold px-6 h-14 hover:brightness-95 transition-[filter]">
                  <MessageCircle className="w-5 h-5" /> +91 95155 62436
                </a>
                <a href={IG_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center justify-center gap-2 border border-white/25 px-6 h-14 font-semibold hover:bg-white hover:text-ink transition-colors">
                  <Instagram className="w-5 h-5" /> @formline2026
                </a>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-white/40">
            <span>© {new Date().getFullYear()} Formline Design Institute. All rights reserved.</span>
            <span className="uppercase tracking-[0.2em]">Draw your way into architecture</span>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="float-whatsapp" className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white grid place-items-center shadow-lg hover:scale-105 transition-transform">
        <MessageCircle className="w-7 h-7" />
      </a>

      <EnquiryDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultProgram={program} />
    </div>
  );
}
